import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "../lib/utils";

/**
 * A horizontal scroll container with a gradient fade at its edges.
 *
 * The fade appears ONLY on a side that still has something behind it (as on the
 * page builder's feedback cards and the shop's product slider), unlike a static
 * mask that softened both edges even when there was nowhere left to scroll. An
 * edge faded with nothing past it promises content that is not there, which is a
 * worse lie than no hint at all.
 *
 * The fade is a CSS mask rather than a coloured overlay, so it is INDEPENDENT of
 * the container's background: it works on the page (`bg-background`) and inside
 * a card (`bg-card`) without being told which.
 *
 * `deps` - the data whose change re-triggers the measurement, for when the
 * content or the widths move and `scrollWidth` moves with them.
 */
const FADE_PX = 40; // how wide the edge softening is

export function ScrollFade({ children, className, deps = [] }: {
  children: ReactNode;
  className?: string;
  deps?: unknown[];
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [left, setLeft] = useState(false);
  const [right, setRight] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    function update() {
      if (!el) return;
      setLeft(el.scrollLeft > 1);
      setRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", update); ro.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Mask the content transparent only on an edge that scrolls. Both false means
  // it fits, so there is no mask at all and the content stays perfectly sharp.
  const mask =
    left || right
      ? `linear-gradient(to right, ${left ? "transparent" : "#000"} 0, #000 ${FADE_PX}px, #000 calc(100% - ${FADE_PX}px), ${right ? "transparent" : "#000"} 100%)`
      : undefined;

  return (
    <div
      ref={ref}
      className={cn(
        // The scrollbar is hidden: the gradient fade already carries the hint,
        // and a bar as well would be the same message drawn twice.
        "overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
      style={mask ? { WebkitMaskImage: mask, maskImage: mask } : undefined}
    >
      {children}
    </div>
  );
}

/**
 * The end of a box that scrolls up and down: a soft fade along its bottom edge
 * while there is more below, and nothing once the end is reached. The same promise
 * as `ScrollFade`, for a box with no bar (`scroll-none`).
 *
 * Put it as the LAST child of the scrolling box. It finds that box by itself and
 * sticks to its bottom edge with no height of its own, so the box needs no wrapper
 * and scrolls exactly as far as before.
 *
 * It is an overlay and not a mask on purpose. A mask would turn the whole box into
 * a stacking context, and a backdrop that opens inside the box (an icon picker's
 * click-away layer) could then no longer cover the rest of the screen. The price is
 * that it has to match the box's background: `from-card` by default, `className`
 * for any other.
 */
export function ScrollEndFade({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [more, setMore] = useState(false);

  useEffect(() => {
    const box = ref.current?.parentElement;
    if (!box) return;
    function update() {
      if (!box) return;
      setMore(box.scrollTop + box.clientHeight < box.scrollHeight - 1);
    }
    // The box keeps its size while its content changes, so the content is watched
    // as well: a child is swapped (a tab) or a child resizes (a late image, a fold).
    const ro = new ResizeObserver(update);
    function watch() {
      if (!box) return;
      ro.disconnect();
      ro.observe(box);
      for (const child of Array.from(box.children)) ro.observe(child);
    }
    const mo = new MutationObserver(watch);
    mo.observe(box, { childList: true });
    watch();
    box.addEventListener("scroll", update, { passive: true });
    return () => { box.removeEventListener("scroll", update); ro.disconnect(); mo.disconnect(); };
  }, []);

  return (
    <div ref={ref} aria-hidden className="pointer-events-none sticky bottom-0 z-20 h-0">
      <div
        className={cn(
          "absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-card to-transparent transition-opacity duration-200",
          more ? "opacity-100" : "opacity-0",
          className,
        )}
      />
    </div>
  );
}
